import { isApiError, type Problem } from '@hive/api';
import { useI18n, type CommonKey, type I18nValue } from '@hive/i18n';
import { Component, type ReactNode } from 'react';
import { Icon } from './icons';

/** Erro explicado no padrão do protótipo e do RF-NOC-03: o que houve, por quê e como resolver. */
export interface Explained {
  title?: string;
  what: ReactNode;
  why?: ReactNode;
  fix?: ReactNode;
  ref?: string;
  /** Campos com problema: rótulo (ou id) → mensagem. */
  fields?: Array<{ field: string; label?: string; message: string }>;
  /** Para o suporte, recolhido em "Detalhes técnicos": código do erro, status HTTP e a mensagem técnica original. */
  tech?: { code?: string; status?: number; message?: string };
}

/**
 * Caixa de erro legível nos quatro temas: fundo neutro da superfície, borda e ícone na cor de alerta, texto na cor
 * normal do texto (nunca vermelho sobre vermelho) e os detalhes técnicos recolhidos, com a referência para o suporte.
 */
export function ErrorBox({ e, actions, id }: { e: Explained; actions?: ReactNode; id?: string }) {
  const { t } = useI18n<CommonKey>();
  const tech = e.tech ?? {};
  const hasTech = !!(e.ref || tech.code || tech.status || tech.message);
  return (
    <div className="err" role="alert" id={id}>
      <div className="err-title">
        <Icon name="alert" />
        <span>{e.title ?? t('err.title')}</span>
      </div>
      <dl>
        <dt>{t('err.what')}</dt>
        <dd>{e.what}</dd>
        {e.why && (
          <>
            <dt>{t('err.why')}</dt>
            <dd>{e.why}</dd>
          </>
        )}
        {e.fix && (
          <>
            <dt>{t('err.fix')}</dt>
            <dd>{e.fix}</dd>
          </>
        )}
      </dl>
      {e.fields && e.fields.length > 0 && (
        <div className="err-fields">
          <b>{t('err.fields')}:</b>
          <ul>
            {e.fields.map((f) => (
              <li key={f.field}>
                <b>{f.label ?? f.field}</b> — {f.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {actions && <div className="row">{actions}</div>}
      {hasTech && (
        <details className="err-tech">
          <summary>{t('err.details')}</summary>
          <dl>
            {e.ref && (
              <>
                <dt>{t('err.refLabel')}</dt>
                <dd>
                  <code>{e.ref}</code> <span className="sr-only">{t('err.ref', { ref: e.ref })}</span>
                </dd>
              </>
            )}
            {tech.code && (
              <>
                <dt>{t('err.code')}</dt>
                <dd>
                  <code>{tech.code}</code>
                  {tech.status ? <code> · HTTP {tech.status}</code> : null}
                </dd>
              </>
            )}
            {tech.message && (
              <>
                <dt>{t('err.message')}</dt>
                <dd>
                  <code className="err-msg">{tech.message}</code>
                </dd>
              </>
            )}
          </dl>
          {e.ref && <p className="xs">{t('err.refHint')}</p>}
        </details>
      )}
    </div>
  );
}

/**
 * Falhas genéricas (sem o problem+json do núcleo, ou antes de chegar nele), cada uma com o seu o quê / por quê / como
 * resolver nos três idiomas (chaves problem.<código>.*).
 */
export const GENERIC_ERRORS = [
  'network',
  'timeout',
  'bad-request',
  'unauthorized',
  'forbidden',
  'not-found',
  'conflict',
  'invalid-fields',
  'rate-limited',
  'unexpected-error',
  'unavailable',
  'bad-response',
] as const;
export type GenericError = (typeof GENERIC_ERRORS)[number];

const BY_STATUS: Record<number, GenericError> = {
  400: 'bad-request',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'not-found',
  408: 'timeout',
  409: 'conflict',
  422: 'invalid-fields',
  429: 'rate-limited',
  500: 'unexpected-error',
  502: 'unavailable',
  503: 'unavailable',
  504: 'timeout',
};

/** Texto que não serve para quem usa: HTML, JSON, pilha de exceção, nome de exceção, "HTTP 500", erro do navegador. */
const TECHNICAL =
  /<\/?[a-z!][^>]*>|^\s*[{[]|\b[\w$.]*(Exception|Error):|\bat [\w$.<>]+\(|\bHTTP\s?\d{3}\b|^\s*\d{3}(\s|$)|\b(ECONNREFUSED|ECONNRESET|ETIMEDOUT|ERR_[A-Z_]+)\b|Failed to fetch|NetworkError|Load failed|Unexpected token|is not valid JSON|undefined|\bnull\b/i;

export function looksTechnical(s: string | undefined | null): boolean {
  return !!s && TECHNICAL.test(s);
}

/** O código do erro: o do núcleo quando ele respondeu com problem+json; senão, a falha genérica pelo status ou pela exceção. */
export function errorCode(err: unknown): string {
  if (isApiError(err)) {
    const cause = (err as { cause?: unknown }).cause;
    if (err.status === 0 && cause !== undefined) return errorCode(cause) === 'unexpected-error' ? 'network' : errorCode(cause);
    if (err.problem.code) return err.problem.code;
    if (err.status === 0) return 'network';
    return BY_STATUS[err.status] ?? (err.status >= 500 ? 'unexpected-error' : 'bad-request');
  }
  const name = err instanceof Error || (typeof DOMException !== 'undefined' && err instanceof DOMException) ? (err as Error).name : '';
  const msg = err instanceof Error ? err.message : '';
  if (name === 'AbortError' || name === 'TimeoutError' || /timed? ?out/i.test(msg)) return 'timeout';
  if (name === 'SyntaxError' && /json|token/i.test(msg)) return 'bad-response';
  if (name === 'TypeError' && /fetch|network|load failed/i.test(msg)) return 'network';
  return 'unexpected-error';
}

const FIELD_MESSAGES: Record<string, CommonKey> = {
  'Required.': 'problem.field.required',
  'Invalid format.': 'problem.field.invalidFormat',
  'Unknown profile.': 'problem.field.unknownProfile',
};

/**
 * Converte qualquer erro em texto no idioma da pessoa, no padrão o quê / por quê / como resolver.
 * O núcleo já responde no idioma da pessoa (Accept-Language) e diz o caso concreto ("quem enviou a versão não pode
 * aprová-la"): usamos o texto dele, e a tradução do código para o pedaço que ele não manda. O que não veio do núcleo
 * (rede, tempo esgotado, proxy respondendo HTML, exceção do navegador) usa só os nossos textos: a mensagem técnica fica
 * nos detalhes recolhidos, para o suporte, e nunca no texto principal.
 */
export function explainProblem(err: unknown, i18n: Pick<I18nValue<CommonKey>, 't' | 'has' | 'lang'>, labels?: Record<string, string>): Explained {
  const { t, has } = i18n;
  const code = errorCode(err);
  const api = isApiError(err) ? err : null;
  const p: Problem = api ? api.problem : {};
  // problem+json do núcleo: tem código e não é a falha de rede montada pelo próprio cliente
  const fromCore = !!api && !!p.code && api.status !== 0;
  const server = (s: string | undefined) => (fromCore && s && !looksTechnical(s) ? s : undefined);
  const ours = (part: string) => {
    const k = `problem.${code}.${part}`;
    return has(k) ? t(k as CommonKey) : undefined;
  };
  // exceção do navegador ou do canal com o agente, em inglês: só serve a quem usa inglês, e só se não for técnica
  const plain = !api && i18n.lang === 'en' && code === 'unexpected-error' && err instanceof Error && err.message && !looksTechnical(err.message) ? err.message : undefined;
  const what = server(p.title ?? p.detail) ?? plain ?? ours('what') ?? t('problem.unexpected-error.what');
  const why = server(p.why) ?? ours('why') ?? (fromCore ? undefined : t('problem.unexpected-error.why'));
  const fix = server(p.fix) ?? ours('fix') ?? (fromCore ? undefined : t('problem.unexpected-error.fix'));
  const fields = p.fields
    ? Object.entries(p.fields).map(([field, message]) => {
        const known = FIELD_MESSAGES[message];
        return { field, label: labels?.[field], message: known ? t(known) : message };
      })
    : undefined;
  const original = api ? p.detail ?? p.title ?? (api.status === 0 ? causeText((api as { cause?: unknown }).cause) : undefined) : causeText(err);
  const message = original && original !== what ? original.slice(0, 300) : undefined;
  return { what, why, fix, ref: p.ref, fields, tech: { code, status: api?.status || undefined, message } };
}

function causeText(e: unknown): string | undefined {
  if (e == null) return undefined;
  if (e instanceof Error) return `${e.name}: ${e.message}`;
  return typeof e === 'string' ? e : undefined;
}

/** Uma linha só, para avisos (toasts) e mensagens curtas: o que houve e, se houver, a referência para o suporte. */
export function friendlyError(err: unknown, i18n: Pick<I18nValue<CommonKey>, 't' | 'has' | 'lang'>): string {
  const e = explainProblem(err, i18n);
  const what = typeof e.what === 'string' ? e.what : i18n.t('problem.unexpected-error.what');
  return e.ref ? `${what} (${i18n.t('err.ref', { ref: e.ref })})` : what;
}

/** O mesmo, como hook: `const errText = useErrorText(); toast(errText(e), { kind: 'crit' })`. */
export function useErrorText(): (err: unknown) => string {
  const i18n = useI18n<CommonKey>();
  return (err) => friendlyError(err, i18n);
}

/** Caixa de erro para qualquer falha da API, já traduzida. */
export function ProblemBox({ error, labels, actions }: { error: unknown; labels?: Record<string, string>; actions?: ReactNode }) {
  const i18n = useI18n<CommonKey>();
  return <ErrorBox e={explainProblem(error, i18n, labels)} actions={actions} />;
}

/** Tela que quebrou ao desenhar: a mesma caixa de erro, com tentar de novo e recarregar (a pilha fica nos detalhes). */
function CrashBox({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const i18n = useI18n<CommonKey>();
  const e = explainProblem(error, i18n);
  return (
    <ErrorBox
      e={{ ...e, title: i18n.t('err.crashTitle') }}
      actions={
        <>
          <button type="button" className="btn btn-primary" onClick={onRetry}>
            {i18n.t('ui.retry')}
          </button>
          <button type="button" className="btn" onClick={() => window.location.reload()}>
            {i18n.t('err.reload')}
          </button>
        </>
      }
    />
  );
}

/**
 * Pega a exceção de uma tela ao desenhar (sem ela a área ficava em branco) e mostra a caixa de erro amigável.
 * Trocar `resetKey` (a rota, por exemplo) limpa o erro.
 */
export class ErrorBoundary extends Component<{ children: ReactNode; resetKey?: unknown }, { error: unknown; key: unknown }> {
  override state = { error: null as unknown, key: this.props.resetKey };

  static getDerivedStateFromError(error: unknown) {
    return { error };
  }

  static getDerivedStateFromProps(p: { resetKey?: unknown }, s: { error: unknown; key: unknown }) {
    return p.resetKey !== s.key ? { error: null, key: p.resetKey } : null;
  }

  override render() {
    if (this.state.error != null) return <CrashBox error={this.state.error} onRetry={() => this.setState({ error: null })} />;
    return this.props.children;
  }
}
